import SalviaLayout from "@/components/SalviaLayout";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";
import { KPICard, KPICardRow, StatusBadge } from "@/components/dashboard";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertTriangle, AlertCircle, Clock, ShieldAlert } from "lucide-react";

export default function CanalPendencias() {
  usePageTitle("Pendências do Canal");
  const { user } = useAuth();
  const isChannelRole = user?.role === "channel_admin" || user?.role === "channel_admin_multi" || user?.role === "channel_operator";
  const { data, isLoading, error } = trpc.dashboards.channel.pendencies.useQuery(undefined, {
    enabled: !!user && isChannelRole,
    refetchOnWindowFocus: false,
  });

  if (error) {
    return (
      <SalviaLayout title="Dashboard — Pendências">
        <Card className="p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-destructive" size={48} />
          <p className="text-muted-foreground">{error.message}</p>
        </Card>
      </SalviaLayout>
    );
  }

  const loading = isLoading || !data;
  const summaryMap: Record<string, { count: number; companies: number }> = {};
  if (data?.summary) {
    for (const s of data.summary) summaryMap[s.status] = { count: s.count, companies: s.companies };
  }

  return (
    <SalviaLayout title="Dashboard — Pendências">
      <div className="space-y-6">
        <KPICardRow>
          <KPICard
            label="Bloq. Sem Respostas"
            value={summaryMap.AVALIACAO_BLOQUEADA_SEM_RESPOSTAS?.count ?? 0}
            icon={AlertCircle}
            iconColor="text-red-600"
            iconBg="bg-red-100"
            subtitle={`${summaryMap.AVALIACAO_BLOQUEADA_SEM_RESPOSTAS?.companies ?? 0} empresas`}
            loading={loading}
          />
          <KPICard
            label="Bloq. Dados Insuf."
            value={summaryMap.AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES?.count ?? 0}
            icon={ShieldAlert}
            iconColor="text-orange-600"
            iconBg="bg-orange-100"
            subtitle={`${summaryMap.AVALIACAO_BLOQUEADA_DADOS_INSUFICIENTES?.companies ?? 0} empresas`}
            loading={loading}
          />
          <KPICard
            label="Pendentes"
            value={summaryMap.PENDENTE?.count ?? 0}
            icon={Clock}
            iconColor="text-slate-600"
            iconBg="bg-slate-100"
            subtitle={`${summaryMap.PENDENTE?.companies ?? 0} empresas`}
            loading={loading}
          />
          <KPICard
            label="Total Pendências"
            value={data?.items.length ?? 0}
            icon={AlertTriangle}
            iconColor="text-amber-600"
            iconBg="bg-amber-100"
            loading={loading}
          />
        </KPICardRow>

        <Card className="p-6">
          <h3 className="text-sm font-semibold mb-4">Detalhamento por Empresa</h3>
          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4,5].map(i => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : data.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma pendência identificada</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Empresa</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">Status</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-muted-foreground uppercase">Qtd</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-3 py-2.5 font-medium">{item.companyName}</td>
                      <td className="px-3 py-2.5"><StatusBadge status={item.status} /></td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </SalviaLayout>
  );
}
